import { useState, useEffect } from 'react'
import { customerApi } from '../services/api'
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  SpeakerWaveIcon,
  CpuChipIcon,
  ArrowPathIcon,
  CalendarIcon,
  ChartPieIcon
} from '@heroicons/react/24/outline'

export default function UsageCostPanel() {
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [summary, setSummary] = useState(null)
  const [dailyUsage, setDailyUsage] = useState([])
  const [costBreakdown, setCostBreakdown] = useState(null)
  const [selectedDays, setSelectedDays] = useState(30)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchUsageData()
  }, [selectedDays])

  const fetchUsageData = async () => {
    try {
      setLoading(true)
      setError('')

      // Fetch all usage data in parallel
      const [summaryRes, dailyRes, breakdownRes] = await Promise.all([
        customerApi.get(`/customer/usage/summary?days=${selectedDays}`),
        customerApi.get(`/customer/usage/daily?days=${selectedDays}`),
        customerApi.get(`/customer/usage/cost-breakdown?days=${selectedDays}`)
      ])

      console.log('✅ Usage data fetched:', { summary: summaryRes.data, daily: dailyRes.data, breakdown: breakdownRes.data })

      setSummary(summaryRes.data)
      setDailyUsage(dailyRes.data)
      setCostBreakdown(breakdownRes.data)
    } catch (err) {
      console.error('❌ Failed to fetch usage data:', err)
      console.error('Error details:', err.response?.data || err.message)

      const errorMsg = err.response?.data?.detail || err.message || 'Unknown error'
      setError(`${errorMsg}. Check console for details.`)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  const handleRefresh = () => {
    setRefreshing(true)
    fetchUsageData()
  }

  const formatCost = (cost) => {
    if (cost === 0) return '$0.00'
    if (cost < 0.001) return `$${cost.toFixed(6)}` // Sub-cent: show 6 decimals
    if (cost < 0.01) return `$${cost.toFixed(4)}`   // Sub-cent: show 4 decimals
    if (cost < 1) return `$${cost.toFixed(3)}`      // Cents: show 3 decimals (e.g., $0.125)
    return `$${cost.toFixed(2)}`                     // Dollars: show 2 decimals (e.g., $1.25)
  }

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toString()
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
        <div className="flex items-center justify-center">
          <ArrowPathIcon className="h-8 w-8 text-blue-600 animate-spin" />
          <span className="ml-3 text-gray-600">Loading usage data...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-xl p-6 border border-red-200">
        <p className="text-red-800">{error}</p>
        <button
          onClick={fetchUsageData}
          className="mt-3 text-red-600 hover:text-red-700 font-medium"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header with date range selector */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg">
            <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Usage & Costs</h3>
            <p className="text-sm text-gray-500">Track your API consumption</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedDays}
            onChange={(e) => setSelectedDays(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh data"
          >
            <ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total Cost */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
          <div className="flex items-center justify-between mb-2">
            <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
            <span className="text-xs font-medium text-green-700">Total</span>
          </div>
          <p className="text-3xl font-bold text-green-900">{formatCost(summary?.total_cost || 0)}</p>
          <p className="text-xs text-green-700 mt-1">Total spend</p>
        </div>

        {/* Conversations */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-blue-600" />
            <span className="text-xs font-medium text-blue-700">Sessions</span>
          </div>
          <p className="text-3xl font-bold text-blue-900">{formatNumber(summary?.total_conversations || 0)}</p>
          <p className="text-xs text-blue-700 mt-1">Conversations</p>
        </div>

        {/* Tokens */}
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-200">
          <div className="flex items-center justify-between mb-2">
            <CpuChipIcon className="h-8 w-8 text-purple-600" />
            <span className="text-xs font-medium text-purple-700">Tokens</span>
          </div>
          <p className="text-3xl font-bold text-purple-900">{formatNumber(summary?.total_tokens || 0)}</p>
          <p className="text-xs text-purple-700 mt-1">LLM tokens</p>
        </div>

        {/* Voice */}
        <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-5 border border-orange-200">
          <div className="flex items-center justify-between mb-2">
            <SpeakerWaveIcon className="h-8 w-8 text-orange-600" />
            <span className="text-xs font-medium text-orange-700">Voice</span>
          </div>
          <p className="text-3xl font-bold text-orange-900">{formatNumber(summary?.voice_characters || 0)}</p>
          <p className="text-xs text-orange-700 mt-1">TTS characters</p>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
          <ChartBarIcon className="h-5 w-5 mr-2 text-blue-600" />
          Cost Breakdown
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* LLM Cost */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">LLM (GPT)</span>
              <CpuChipIcon className="h-5 w-5 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-900">{formatCost(summary?.llm_cost || 0)}</p>
            <p className="text-xs text-blue-700 mt-1">
              {((summary?.llm_cost / summary?.total_cost * 100) || 0).toFixed(1)}% of total
            </p>
          </div>

          {/* Embedding Cost */}
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-purple-900">Embeddings</span>
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-900">{formatCost(summary?.embedding_cost || 0)}</p>
            <p className="text-xs text-purple-700 mt-1">
              {((summary?.embedding_cost / summary?.total_cost * 100) || 0).toFixed(1)}% of total
            </p>
          </div>

          {/* Voice Cost */}
          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-orange-900">Voice (TTS)</span>
              <SpeakerWaveIcon className="h-5 w-5 text-orange-600" />
            </div>
            <p className="text-2xl font-bold text-orange-900">{formatCost(summary?.voice_cost || 0)}</p>
            <p className="text-xs text-orange-700 mt-1">
              {((summary?.voice_cost / summary?.total_cost * 100) || 0).toFixed(1)}% of total
            </p>
          </div>
        </div>
      </div>

      {/* Model Breakdown */}
      {costBreakdown?.by_model && costBreakdown.by_model.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <ChartPieIcon className="h-5 w-5 mr-2 text-purple-600" />
            Usage by Model
          </h4>
          <div className="space-y-3">
            {costBreakdown.by_model.map((model, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{model.model}</p>
                  <p className="text-xs text-gray-500">
                    {formatNumber(model.total_tokens)} tokens • {model.conversations} conversations
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-900">{formatCost(model.total_cost)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily Usage Chart */}
      {dailyUsage && dailyUsage.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
          <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2 text-green-600" />
            Daily Usage Trend
          </h4>
          <div className="space-y-2">
            {dailyUsage.slice().reverse().slice(0, 14).map((day, index) => {
              const maxCost = Math.max(...dailyUsage.map(d => d.costs.total))
              const barWidth = maxCost > 0 ? (day.costs.total / maxCost * 100) : 0

              return (
                <div key={index} className="flex items-center space-x-3">
                  <span className="text-xs text-gray-500 w-20">{day.date}</span>
                  <div className="flex-1 h-8 bg-gray-100 rounded-lg overflow-hidden relative">
                    <div
                      className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all duration-300"
                      style={{ width: `${barWidth}%` }}
                    ></div>
                    <span className="absolute inset-0 flex items-center justify-end pr-3 text-xs font-medium text-gray-700">
                      {formatCost(day.costs.total)}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 w-16 text-right">
                    {day.conversations.total} conv
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
